import * as React from 'react';

import { theme } from '@styles/theme';
import { roundToDecimal } from '@utils/numbers';

import { ConfettiExplosionStyles, IParticle } from './styles';
import { coinFlip, mapRange, rotate, rotationTransforms, shouldBeCircle } from './utils';

const ROTATION_SPEED_MIN = 200; // minimum possible duration of single particle full rotation
const ROTATION_SPEED_MAX = 800; // maximum possible duration of single particle full rotation
const CRAZY_PARTICLES_FREQUENCY = 0.1; // 0-1 frequency of crazy curvy unpredictable particles
const CRAZY_PARTICLE_CRAZINESS = 0.25; // 0-1 how crazy these crazy particles are
const BEZIER_MEDIAN = 0.5; // utility for mid-point bezier curves, to ensure smooth motion paths
const DECIMAL_POINTS = 4;
const FORCE = 0.2; // 0-1 roughly the vertical force at which particles initially explode
const SIZE = 8; // max height for particle rectangles, diameter for particle circles
const HEIGHT = '320vh'; // distance particles will fall from initial explosion point
const WIDTH = 1600; // horizontal spread of particles in pixels
const PARTICLE_COUNT = 200;
const DURATION = 4200;
const COLORS = [
  theme.color.purple100,
  theme.color.green100,
  theme.color.orange100,
  theme.color.elementBlue100,
  theme.color.red100,
  theme.color.yellow100,
  theme.color.teal100,
  theme.color.indigo100,
];

const getRotationName = (index: number) => `rotate-${index}`;
const getConfettiXName = (index: number) => `confetti-x-${index}`;
const getConfettiYName = (index: number) => `confetti-y-${index}`;

export const injectKeyframes = ({ particles }: { particles: IParticle[] }) => {
  const style = document.createElement('style');
  style.id = 'confetti-keyframes';
  document.head.appendChild(style);

  const sheet = style.sheet!;

  // Inject rotation keyframes
  rotationTransforms.forEach((xyz, index) => {
    const rule = `
        @keyframes ${getRotationName(index)} {
          50% {
            transform: rotate3d(${xyz.map((v) => v / 2).join(',')}, 180deg);
          }
          100% {
            transform: rotate3d(${xyz.join(',')}, 360deg);
          }
        }
      `;
    sheet.insertRule(rule, sheet.cssRules.length);
  });

  // Inject X and Y animation keyframes per particle
  particles.forEach((particle) => {
    const landingX = mapRange({
      value: Math.abs(rotate(particle.degree, 90) - 180),
      x1: 0,
      y1: 180,
      x2: -WIDTH / 2,
      y2: WIDTH / 2,
    });

    const xRule = `
        @keyframes ${particle.confettiX} {
          to {
            transform: translateX(${landingX}px);
          }
        }
      `;
    const yRule = `
        @keyframes ${particle.confettiY} {
          to {
            transform: translateY(${HEIGHT});
          }
        }
      `;
    sheet.insertRule(xRule, sheet.cssRules.length);
    sheet.insertRule(yRule, sheet.cssRules.length);
  });
};

export interface ConfettiProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'ref'> {
  onComplete?: () => void;
}

export const createParticles = (): IParticle[] => {
  const degreeIncrement = 360 / PARTICLE_COUNT;

  return Array.from({ length: PARTICLE_COUNT }, (_, index) => {
    const rotationDuration = Math.round(
      Math.random() * (ROTATION_SPEED_MAX - ROTATION_SPEED_MIN) + ROTATION_SPEED_MIN
    );
    const rotationIndex = Math.round(Math.random() * (rotationTransforms.length - 1));

    const durationChaos = DURATION - Math.round(Math.random() * 1000);
    const shouldBeCrazy = Math.random() < CRAZY_PARTICLES_FREQUENCY;
    const isCircle = shouldBeCircle(rotationIndex);

    // x-axis disturbance, roughly the distance the particle will initially deviate from its target
    const x1 = shouldBeCrazy ? Math.round(Math.random() * CRAZY_PARTICLE_CRAZINESS * 100) / 100 : 0;
    const x2 = x1 * -1;
    const x3 = x1;
    // x-axis arc of explosion, so 90deg and 270deg particles have curve of 1, 0deg and 180deg have 0
    const x4 = roundToDecimal(
      Math.abs(
        mapRange({
          value: Math.abs(rotate(degreeIncrement, 90) - 180),
          x1: 0,
          y1: 180,
          x2: -1,
          y2: 1,
        })
      ),
      DECIMAL_POINTS
    );

    // roughly how fast particle reaches end of its explosion curve
    const y1 = roundToDecimal(Math.random() * BEZIER_MEDIAN, DECIMAL_POINTS);
    // roughly maps to the distance particle goes before reaching free-fall
    const y2 = roundToDecimal(Math.random() * FORCE * (coinFlip() ? 1 : -1), DECIMAL_POINTS);
    // roughly how soon the particle transitions from explosion to free-fall
    const y3 = BEZIER_MEDIAN;
    // roughly the ease of free-fall
    const y4 = roundToDecimal(
      Math.max(
        mapRange({
          value: Math.abs(degreeIncrement - 180),
          x1: 0,
          y1: 180,
          x2: FORCE,
          y2: -FORCE,
        }),
        0
      ),
      DECIMAL_POINTS
    );

    const widthPx = isCircle ? SIZE : Math.round(Math.random() * 4) + SIZE / 2;
    const heightPx = isCircle ? SIZE : Math.round(Math.random() * 2) + SIZE;

    const rotationName = getRotationName(rotationIndex);
    const confettiX = getConfettiXName(index);
    const confettiY = getConfettiYName(index);

    return {
      color: COLORS[index % COLORS.length],
      degree: degreeIncrement * index,
      rotationDuration,
      durationChaos,
      x1,
      x2,
      x3,
      x4,
      y1,
      y2,
      y3,
      y4,
      isCircle,
      width: widthPx,
      height: heightPx,
      rotationName,
      confettiX,
      confettiY,
    };
  });
};

type ConfettiExplosionProps = ConfettiProps & {
  particles: Array<IParticle>;
};

export function ConfettiExplosion({ onComplete, particles }: ConfettiExplosionProps) {
  React.useEffect(() => {
    if (typeof onComplete === 'function') {
      const timeout = setTimeout(onComplete, DURATION);
      return () => clearTimeout(timeout);
    }
  }, [onComplete]);

  return <ConfettiExplosionStyles particles={particles} />;
}
